import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, IndustryHero, IndustryIntro, UseCasesGrid,
  IndustryStats, IndustryDeliverables, IndustryCTA, Footer,
} from '../shared/IndustryPageSections';

const COLOR = '#1565A7';

const USE_CASES = [
  { icon:'cpu', title:'STEM & IoT Learning Kits', to:'/industries/education/stem-iot-learning-kits', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Grade-Specific Kits with Sensors & Microcontrollers</li>
      <li>Curriculum-Mapped Lesson Plans & Activity Manuals</li>
      <li>Pre-Configured Coding Environments (Scratch, Python, Blockly)</li>
    </ul>
  ) },
  { icon:'automation', title:'Robotics & Automation Labs', to:'/industries/education/robotics-automation-labs', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Complete Lab Setup — Hardware, Software & Furniture</li>
      <li>Programming Platforms in Python, C++ & Visual Coding</li>
      <li>Competition-Ready Kits for FIRST, RoboCup & National Events</li>
    </ul>
  ) },
  { icon:'tool', title:'Engineering Lab Development', to:'/industries/education/engineering-lab-development', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>Turnkey Labs for Mechanical, Electrical, CS & ECE Departments</li>
      <li>Industry-Standard Software and Hardware Ecosystems</li>
      <li>Lab Manuals Aligned with AICTE, NBA & NAAC Norms</li>
    </ul>
  ) },
  { icon:'cloud', title:'Cloud-Based Learning Platforms', to:'/industries/education/cloud-based-learning-platforms', desc:(
    <ul style={{ margin:0, paddingLeft:16, display:'flex', flexDirection:'column', gap:4 }}>
      <li>White-Label LMS with Live Video & Digital Library</li>
      <li>AI-Powered Adaptive Learning Paths & Doubt Resolution</li>
      <li>Parent Portals & Learning Analytics Dashboards</li>
    </ul>
  ) },
];

const STATS = [
  { val:'$3.5B', label:'Global smart classroom & EdTech market by 2028' },
  { val:'40%', label:'Better concept retention with hands-on STEM kits' },
  { val:'3×', label:'Faster skills readiness with lab-based curricula' },
];

const DELIVERABLES = [
  'Grade-specific STEM kits with sensors, microcontrollers, and actuators',
  'Curriculum-mapped lesson plans and teacher training',
  'Complete robotics lab setup with hardware, software, and furniture',
  'Competition-ready kits for FIRST, RoboCup, and national-level events',
  'Turnkey engineering labs for mechanical, electrical, CS, and ECE departments',
  'Custom lab manuals aligned with AICTE, NBA, and NAAC norms',
  'White-label LMS with live video, recorded lectures, and digital library',
  'AI-powered adaptive learning paths and learning analytics dashboards',
];

const INTRO_BODY = [
  "Classrooms across India are still teaching tomorrow's engineers with yesterday's tools — textbook diagrams standing in for circuits students could be building with their own hands. IoT-powered STEM kits and robotics labs change that, turning abstract concepts into projects students can build, code, and compete with.",
  "Zone-X works with schools, colleges, and universities to build STEM and IoT learning kits, robotics and automation labs, industry-ready engineering labs, and cloud-based learning platforms — closing the gap between what's taught in the classroom and what graduates need to be job-ready from day one.",
];

export default function EducationPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Solutions for Education India | STEM Kits, Robotics Labs & Learning Platforms — Zone-X</title>
        <meta name="description" content="Zone-X builds education IoT solutions in India — STEM and IoT learning kits, robotics and automation labs, engineering lab development, and cloud-based learning platforms for schools and universities." />
        <link rel="canonical" href="https://zone-x.dev/industries/education" />
      </Helmet>

      <div ref={heroRef}>
        <IndustryHero
          tag="Industry / Education"
          breadcrumb="Education"
          title="Build Classrooms That"
          titleAccent="Build Engineers."
          subtitle="Zone-X designs STEM kits, robotics labs, engineering labs, and cloud learning platforms that turn students into builders — not just test-takers."
          color={COLOR}
          visible={heroVisible}
          visual="/visuals/industries/Industry 6.png"
        />
      </div>

      <IndustryIntro heading="Why Hands-On IoT Learning Is the Future of Education in India" body={INTRO_BODY} color={COLOR} />
      <UseCasesGrid cases={USE_CASES} color={COLOR} />
      <IndustryStats stats={STATS} color={COLOR} />
      <IndustryDeliverables items={DELIVERABLES} color={COLOR} />
      <IndustryCTA />
      <Footer />
    </>
  );
}
